import { css } from '@emotion/react';
import { shouldNotForwardProps } from '@polestar/component-warehouse-react/helpers/shouldForwardProps';
import styled from '@emotion/styled';
import React, { forwardRef } from 'react';
import { useDirection } from '@polestar/component-warehouse-react/contexts/direction';
import { createCSSTransition, MotionDurations, MotionEasings, } from '@polestar/component-warehouse-react/tokens/motion/Motion';
import { useButtonMotion } from '@polestar/component-warehouse-react/components/button/internal/buttonMotion';
import { newFontOverride } from '@polestar/component-warehouse-react/tokens/fonts/Fonts';
// Can't use createCustomFontSize inside an enum
export var IconSizes;
(function (IconSizes) {
    IconSizes["small"] = "0.6667rem";
    IconSizes["medium"] = "1rem";
    IconSizes["large"] = "1.333rem";
    IconSizes["xLarge"] = "1.833rem";
})(IconSizes || (IconSizes = {}));
const Icon = forwardRef(({ icon, pairedWithText = true, topOffset = true, iconProps, ...props }, ref) => {
    const { isRTL } = useDirection();
    // Use the animated icon when we are in a motion context
    const { motion } = useButtonMotion();
    if (!icon || (!pairedWithText && !icon.glyph)) {
        // eslint-disable-next-line no-console
        console.warn('[Icon] Given icon property is not a valid IconData object', {
            icon: icon === null || icon === void 0 ? void 0 : icon.glyph,
            pairedWithText,
        });
        return null;
    }
    const IconComponent = pairedWithText && icon.textPairingGlyph
        ? (motion ? icon.animatedTextPairingGlyph : null) || icon.textPairingGlyph
        : (motion ? icon.animatedGlyph : null) || icon.glyph;
    const iconComponentProps = {
        ...iconProps,
        height: pairedWithText && icon.textPairingGlyph ? '1em' : '100%',
    };
    return (React.createElement(Container, { "aria-hidden": true, ...props, ref: ref, topOffset: pairedWithText && topOffset ? true : undefined, mirrored: isRTL && icon.followDirection ? true : undefined },
        React.createElement(IconComponent, { ...iconComponentProps, role: "presentation" })));
});
Icon.displayName = 'Icon';
export default Icon;
const Container = styled('span', shouldNotForwardProps(true, 'icon', 'color', 'opacity', 'size', 'mirrored', 'topOffset'))(({ size, color, opacity, mirrored, topOffset = false }) => css(css `
      font-size: ${size || '1em'};
      ${`height: calc(1.5 * ${
// @ts-ignore
(IconSizes === null || IconSizes === void 0 ? void 0 : IconSizes[size]) || size || '1em'});`}
      user-select: none;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      transition: ${createCSSTransition('color', MotionEasings.default, MotionDurations.fast)};
    `, topOffset &&
    !mirrored &&
    // this is mostly meant for arrows but breaks for close/cross icon and other icons - will be updated in the future
    css `
        > svg {
          transform: translateY(-1px);
        }
        ${newFontOverride(css `
          transform: translateY(0);
        `)}
      `, color &&
    css `
        color: ${color};
      `, opacity &&
    css `
        opacity: ${opacity};
      `, mirrored &&
    css `
        svg {
          transform: scaleX(-1) ${topOffset ? `translateY(-6.25%)` : ''};

          /* @ts-ignore Selector ignore-direction is never used */
          .ignore-direction {
            scale: -1 1;
            transform-origin: center;
          }
        }
      `));
